// «Abrir el hilo en una ventana» (Mac MacThreadWindow, PARITY P317): a thread in a browser window
// of its own.
//
// The web's one-tab rule (bridge/client.ts: one tab owns the database through a Web Lock) means a
// second page cannot start its own worker. So the thread window is a POPUP of this page
// (`window.open`, same origin, opener kept): it renders its own React tree and its own document, but
// its `data` client borrows the opener's worker through `window.opener.__satoriThreadHost` —
// calls go to the opener's DataClient, change and signal listeners are registered there and
// dropped the moment the popup closes. Answers are structured-cloned INTO the popup's realm so
// nothing in the popup holds an object of the opener's (a `Set`/`Object` from another realm breaks
// identity checks and structural sharing).
//
// The opener closes its thread windows on `pagehide` (they cannot outlive the worker they borrow);
// a popup that loses its host anyway (opened by hand, the opener reloaded) says so instead of
// guessing. Shell actions from the popup (open a conversation, a profile…) run in the main window,
// which is brought to the front.
import { useEffect, useMemo, useState } from 'react';
import { data, type RemoteDataClient } from '../../../bridge/client';
import type { StoreTable } from '../../../data/db/database';
import type { EngineSignal } from '../../../data/sync/syncEngine';
import { Icon } from '../../../design/icons';
import { t } from '../../../i18n/i18n';
import { ThreadPane } from '../../conversation/ThreadPane';
import { ShellContext, type Shell } from '../../shell/ShellContext';
import './extras.css';

const PARAM = 'thread-window';

interface ThreadWindowHost {
  call(method: string, args: unknown[]): Promise<unknown>;
  onChanges(win: Window, listener: (tables: StoreTable[]) => void): () => void;
  onSignal(win: Window, listener: (signal: EngineSignal) => void): () => void;
  shell(): Shell | null;
  focus(): void;
}

type HostWindow = Window & { __satoriThreadHost?: ThreadWindowHost };

// MARK: - The opener (the tab that owns the database)

let currentShell: Shell | null = null;
const opened = new Set<Window>();
let hostInstalled = false;

function installHost(): void {
  if (hostInstalled) return;
  hostInstalled = true;
  const guard = <A extends unknown[]>(win: Window, fn: (...a: A) => void, off: () => void) => (...a: A) => {
    if (win.closed) return off();
    try {
      fn(...a);
    } catch {
      off(); // the popup's realm is gone
    }
  };
  (window as HostWindow).__satoriThreadHost = {
    call: (method, args) => data.callUntyped(method, ...args),
    onChanges(win, listener) {
      let off = () => undefined as unknown;
      off = data.onChanges(guard(win, (tables: Set<StoreTable>) => listener([...tables]), () => off()));
      return () => void off();
    },
    onSignal(win, listener) {
      let off = () => undefined as unknown;
      off = data.onSignal(guard(win, (s: EngineSignal) => listener(s), () => off()));
      return () => void off();
    },
    shell: () => currentShell,
    focus: () => window.focus(),
  };
  // A popup cannot outlive the worker it borrows.
  window.addEventListener('pagehide', () => {
    for (const w of opened) w.close();
    opened.clear();
  });
}

/** The workspace keeps the host's shell current (ExtrasHost). */
export function publishThreadWindowHost(shell: Shell | null): void {
  installHost();
  currentShell = shell;
}

/** Opens (or brings back) the thread's own window. False when the browser refused the popup. */
export function openThreadWindow(rootId: string): boolean {
  installHost();
  const url = `${location.origin}/?${PARAM}=${encodeURIComponent(rootId)}${new URLSearchParams(location.search).has('server') ? `&server=${encodeURIComponent(new URLSearchParams(location.search).get('server')!)}` : ''}`;
  const w = window.open(url, `satori-thread-${rootId}`, 'popup,width=520,height=780');
  if (!w) return false;
  opened.add(w);
  w.focus();
  return true;
}

/** The page IS a thread window (the popup), whatever happens to its host later. */
export function isThreadWindow(): boolean {
  return new URLSearchParams(location.search).has(PARAM);
}

/** The menu item / header button: opens the window, or says the popup was blocked. */
export function useOpenThreadWindow(onBlocked: (message: string) => void): (rootId: string) => void {
  return (rootId) => {
    if (!openThreadWindow(rootId)) onBlocked(t('web.extras.threadWindow.blocked'));
  };
}

/** The thread pane's header button (hidden inside the thread window itself). */
export function ThreadWindowButton({ rootId, onBlocked }: { rootId: string; onBlocked(message: string): void }) {
  const open = useOpenThreadWindow(onBlocked);
  if (isThreadWindow()) return null;
  return (
    <button className="icon-button thread-window-button" onClick={() => open(rootId)} title={t('mac.openInWindow')} aria-label={t('mac.openInWindow')}>
      <Icon name="external" size={16} />
    </button>
  );
}

// MARK: - The popup

function host(): ThreadWindowHost | null {
  try {
    const opener = window.opener as HostWindow | null;
    if (!opener || opener.closed) return null;
    return opener.__satoriThreadHost ?? null;
  } catch {
    return null;
  }
}

/** The thread this popup shows, or null when the page is not a thread window. */
export function threadWindowRootId(): string | null {
  return new URLSearchParams(location.search).get(PARAM);
}

/**
 * Points this page's `data` at the opener's worker. False when there is no host (the popup was
 * opened by hand or its opener is gone) — the page then only explains itself.
 */
export function installThreadWindowClient(): boolean {
  const h = host();
  if (!h) return false;
  const remote: RemoteDataClient = {
    call: (method, args) =>
      new Promise((resolve, reject) => {
        h.call(method, args).then(
          (value) => resolve(value === undefined ? value : structuredClone(value)),
          (error: unknown) => reject(new Error(String((error as Error)?.message ?? error))),
        );
      }),
    onChanges: (listener) => h.onChanges(window, (tables) => listener(new Set(tables))),
    onSignal: (listener) => h.onSignal(window, (s) => listener(structuredClone(s))),
  };
  data.useRemote(remote);
  return true;
}

/** The popup's whole page: the thread pane, full size, with the main window's shell behind it. */
export function ThreadWindowApp({ rootId }: { rootId: string }) {
  const [alive, setAlive] = useState(() => host() !== null);
  const [toast, setToast] = useState<{ text: string; tone: 'info' | 'error' } | null>(null);
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    // The opener reloading or closing ends the borrowed worker: say so (polled — no event crosses).
    const id = setInterval(() => setAlive(host() !== null), 1500);
    return () => {
      window.removeEventListener('resize', onResize);
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(id);
  }, [toast]);

  const remoteShell = alive ? host()?.shell() ?? null : null;
  const localToast = (text: string, tone: 'info' | 'error' = 'info') => setToast({ text, tone });
  // The main window's shell, its actions run THERE (brought to the front); toasts stay here.
  const shell: Shell | null = useMemo(() => {
    if (!remoteShell) return null;
    const forward =
      <A extends unknown[]>(fn: (...a: A) => void) =>
      (...a: A) => {
        host()?.focus();
        fn(...a);
      };
    return {
      team: structuredClone(remoteShell.team),
      me: structuredClone(remoteShell.me),
      teams: structuredClone(remoteShell.teams),
      openChannel: forward(remoteShell.openChannel),
      openThread: forward(remoteShell.openThread),
      openThreadInConversation: forward(remoteShell.openThreadInConversation),
      jumpToPost: forward(remoteShell.jumpToPost),
      openSidePanel: forward(remoteShell.openSidePanel),
      openDestination: forward(remoteShell.openDestination),
      openSearch: forward(remoteShell.openSearch),
      openDialog: forward(remoteShell.openDialog),
      openUser: forward(remoteShell.openUser),
      openDirectMessage: forward(remoteShell.openDirectMessage),
      toast: localToast,
      openSettings: forward(remoteShell.openSettings),
      openHome: forward(remoteShell.openHome),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteShell?.team.id, remoteShell?.me.id, alive]);

  useEffect(() => {
    document.title = `${t('channel.threadTitle')} · Satori Chat`;
  }, []);

  if (!alive || !shell) {
    return (
      <main className="center-screen thread-window-gone">
        <div className="wallpaper" />
        <div className="card">
          <p className="lede">{t('web.extras.threadWindow.hostGone')}</p>
          <button className="primary-button" onClick={() => window.close()}>
            {t('web.extras.threadWindow.close')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <ShellContext.Provider value={shell}>
      <div className="thread-window">
        <ThreadPane
          rootId={rootId}
          me={shell.me.id}
          myUsername={shell.me.username}
          teamName={shell.team.name}
          width={width}
          onClose={() => window.close()}
          onOpenUser={(username, anchor) => shell.openUser({ username }, anchor)}
          onOpenUserId={(userId, anchor) => shell.openUser({ userId }, anchor)}
          onToast={localToast}
        />
      </div>
      {toast ? (
        <div className={`toast${toast.tone === 'error' ? ' error' : ''}`} role="status">
          {toast.text}
        </div>
      ) : null}
    </ShellContext.Provider>
  );
}
